import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../utils/currency';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const daysOverdue = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : 0);

/**
 * What each charge was billed, what has been paid against it, and what is still
 * owed — so rent, water, electricity and garbage are visible separately rather
 * than collapsed into a single outstanding figure.
 *
 * scope: 'tenant' | 'property'
 */
const ChargeBreakdown = ({ scope, id, title = 'Charges' }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(false);
      try {
        const { data: d } = await axios.get(`/api/charges/${scope}/${id}`);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, id]);

  if (loading) {
    return (
      <div className="rounded-xl border border-neutral-200/70 bg-white p-6">
        <div className="h-4 w-28 animate-pulse rounded bg-neutral-100" />
        <div className="mt-4 space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-neutral-50" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-neutral-200/70 bg-white p-6">
        <h3 className="text-sm font-medium text-neutral-900">{title}</h3>
        <p className="mt-2 text-sm text-neutral-500">Couldn't load the charge breakdown.</p>
      </div>
    );
  }

  const charges = data && data.charges ? data.charges : [];
  const totals = (data && data.totals) || { billed: 0, paid: 0, balance: 0 };

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200/70 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3 sm:px-6">
        <div>
          <h3 className="text-sm font-medium text-neutral-900">{title}</h3>
          <p className="text-xs text-neutral-500">Billed, paid and outstanding per charge</p>
        </div>
        {totals.balance > 0 && (
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-neutral-500">Outstanding</p>
            <p className="text-base font-semibold tabular-nums text-danger-700">{formatCurrency(totals.balance)}</p>
          </div>
        )}
      </div>

      {data && data.unavailable ? (
        <p className="px-4 py-8 text-center text-sm text-neutral-500 sm:px-6">
          Named charges aren't set up yet. Run <code className="font-mono text-xs">database/charges_migration.sql</code> to
          break rent, water and electricity out separately.
        </p>
      ) : charges.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-neutral-500 sm:px-6">Nothing has been billed yet.</p>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="border-b border-neutral-100 text-left text-[11px] uppercase tracking-wide text-neutral-500">
                <th className="px-4 py-2 font-medium sm:px-6">Charge</th>
                <th className="px-4 py-2 text-right font-medium">Billed</th>
                <th className="px-4 py-2 text-right font-medium">Paid</th>
                <th className="px-4 py-2 text-right font-medium sm:pr-6">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {charges.map((c) => {
                const overdue = c.balance > 0 ? daysOverdue(c.oldest_unpaid_due) : 0;
                return (
                  <tr key={`${c.code}-${c.charge_type_id}`} className="align-top">
                    <td className="px-4 py-3 sm:px-6">
                      <div className="font-medium text-neutral-900">{c.name}</div>
                      <div className="text-xs text-neutral-500">
                        {c.invoice_count} invoice{c.invoice_count === 1 ? '' : 's'}
                        {c.balance > 0 && c.oldest_unpaid_due ? (
                          <>
                            {' · oldest due '}{fmtDate(c.oldest_unpaid_due)}
                            {overdue > 0 ? <span className="text-danger-600">{` (${overdue}d overdue)`}</span> : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{formatCurrency(c.billed)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-success-700">{formatCurrency(c.paid)}</td>
                    <td className={`px-4 py-3 text-right font-medium tabular-nums sm:pr-6 ${c.balance > 0 ? 'text-danger-700' : 'text-neutral-400'}`}>
                      {c.balance > 0 ? formatCurrency(c.balance) : 'Clear'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-neutral-200 bg-neutral-50/60 font-medium">
                <td className="px-4 py-3 text-neutral-900 sm:px-6">Total</td>
                <td className="px-4 py-3 text-right tabular-nums text-neutral-900">{formatCurrency(totals.billed)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-success-700">{formatCurrency(totals.paid)}</td>
                <td className={`px-4 py-3 text-right tabular-nums sm:pr-6 ${totals.balance > 0 ? 'text-danger-700' : 'text-neutral-500'}`}>
                  {totals.balance > 0 ? formatCurrency(totals.balance) : 'Clear'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChargeBreakdown;
